// Route manifest — the ONE hand-maintained list of console top-level paths.
// Everything else derives from it: App.tsx index routes, desktop rail groups,
// mobile primary/overflow, command palette entries. Add a route by adding one entry —
// nav.test.ts fails if a visible route is unreachable from mobile chrome.
//
// Array order IS display order, grouped by job stage: solo first, then
// workflow (the model lifecycle in JTBD order: upload → data → train → eval →
// deploy — mirrors the dashboard step cards), then integrate (the "call it"
// surfaces — playground for trying, api-keys as the canonical key manager),
// then observe (runtime signals), then account (org admin + spend). Palette
// page order follows the same grouping.
export type NavItem = {
  to: string
  labelKey: string
  subKey: string
  keywords?: string
}

export type RouteMeta = NavItem & {
  /** 'solo' renders ungrouped above the groups (dashboard). */
  group: 'solo' | 'workflow' | 'integrate' | 'observe' | 'account'
  /** Mobile bottom-bar placement. */
  mobile: 'primary' | 'overflow'
}

export const ROUTE_MANIFEST = [
  { to: '/dashboard', labelKey: 'nav.dashboard', subKey: 'nav.dashboardSub', keywords: 'zonglan dashboard zl 总览', group: 'solo', mobile: 'primary' },
  { to: '/models', labelKey: 'nav.models', subKey: 'nav.modelsSub', keywords: 'moxing models mx 模型', group: 'workflow', mobile: 'primary' },
  { to: '/datasets', labelKey: 'nav.dataPrep', subKey: 'nav.dataPrepSub', keywords: 'shujuzhunbei datasets sjzb 数据准备 数据集', group: 'workflow', mobile: 'overflow' },
  { to: '/training', labelKey: 'nav.tuning', subKey: 'nav.tuningSub', keywords: 'moxing tiaoyou training tiaoyou lora 微调 训练', group: 'workflow', mobile: 'primary' },
  { to: '/eval', labelKey: 'nav.eval', subKey: 'nav.evalSub', keywords: 'pinggu eval evaluation benchmark dabi 评测 基准 对比', group: 'workflow', mobile: 'overflow' },
  { to: '/deployments', labelKey: 'nav.deploy', subKey: 'nav.deploySub', keywords: 'moxing shangxian deployments sx 模型上线 部署', group: 'workflow', mobile: 'primary' },
  // playground mobile:'overflow' — 演练场胶囊是底栏最宽一项，留作 primary 会在
  // 390px 视口被右端渐隐裁切、≤360px 整个滚出视野；收进「更多」抽屉保证可达。
  { to: '/playground', labelKey: 'nav.integration', subKey: 'nav.integrationSub', keywords: 'yingyong jicheng playground jicheng 应用集成 演练场', group: 'integrate', mobile: 'overflow' },
  { to: '/api-keys', labelKey: 'nav.keys', subKey: 'nav.keysSub', keywords: 'miyue miyao keys apikeys my 密钥', group: 'integrate', mobile: 'overflow' },
  { to: '/observability', labelKey: 'nav.observability', subKey: 'nav.observabilitySub', keywords: 'guanche guance observability gc 观测 监控', group: 'observe', mobile: 'overflow' },
  { to: '/notifications', labelKey: 'nav.notifications', subKey: 'nav.notificationsSub', keywords: 'tongzhi xiaoxi notifications inbox tzxx 通知 消息 收件箱', group: 'observe', mobile: 'overflow' },
  { to: '/audit', labelKey: 'nav.audit', subKey: 'nav.auditSub', keywords: 'shenji audit auditlog shj 审计 日志 操作记录', group: 'observe', mobile: 'overflow' },
  { to: '/projects', labelKey: 'nav.projects', subKey: 'nav.projectsSub', keywords: 'jiaofang xiangmu projects client workbench jf 交付 项目 甲方 工作台', group: 'account', mobile: 'overflow' },
  { to: '/members', labelKey: 'nav.members', subKey: 'nav.membersSub', keywords: 'chengyuan members org team cy 组织 成员 团队', group: 'account', mobile: 'overflow' },
  { to: '/billing', labelKey: 'nav.billing', subKey: 'nav.billingSub', keywords: 'zhangdan billing zd 账单 费用 用量', group: 'account', mobile: 'overflow' },
  { to: '/contracts', labelKey: 'nav.contracts', subKey: 'nav.contractsSub', keywords: 'hetong jiesuan contracts orders ht 合同 结算 订单 余额', group: 'account', mobile: 'overflow' },
] as const satisfies readonly RouteMeta[]

/** Absolute paths of manifest entries — App.tsx keys its component map by this. */
export type NavPath = (typeof ROUTE_MANIFEST)[number]['to']

const toNavItem = ({ to, labelKey, subKey, keywords }: RouteMeta): NavItem => ({ to, labelKey, subKey, keywords })
// Derivations read the widened RouteMeta view; the `as const` literals only feed NavPath above.
const manifest: readonly RouteMeta[] = ROUTE_MANIFEST

export const NAV_ITEMS: NavItem[] = manifest.map(toNavItem)

export const NAV_GROUPS: { headingKey: string; items: NavItem[] }[] = (
  ['workflow', 'integrate', 'observe', 'account'] as const
).map((g) => ({
  headingKey: `nav.groups.${g}`,
  items: manifest.filter((e) => e.group === g).map(toNavItem),
}))

/** Mobile bottom bar — coverage of every NAV_ITEMS entry is asserted in nav.test.ts. */
export const MOBILE_PRIMARY: NavItem[] = manifest.filter((e) => e.mobile === 'primary').map(toNavItem)
export const MOBILE_OVERFLOW: NavItem[] = manifest.filter((e) => e.mobile === 'overflow').map(toNavItem)
